import {
  GECKO_CONSTS,
  GeckoCardTable,
  GeckoToastService,
  GeckoModalService,
  GeckoModal,
} from 'ui';
import React, { useEffect, useState } from 'react';
import GeckoRequest from '../../../services/gecko-request';
import { useSelector } from 'react-redux';
import Loader from '../../../components/Loader';
import GeckoParser from 'services/gecko-parser';
import GeckoPagination from 'services/gecko-pagination';
import { renderError } from 'services/gecko-error';
import EditDataModal from './EditDataModal';

const useDataSource = (dataSourceId, onError) => {
  const [dataSource, setDataSource] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const refreshDataSource = () => {
    setIsLoading(true);
    return GeckoRequest.get(`/datasource/${dataSourceId}`)
      .then(setDataSource)
      .catch(onError)
      .finally(() => setIsLoading(false));
  };

  useEffect(() => {
    refreshDataSource();
  }, [dataSourceId]);
  return [dataSource, isLoading, refreshDataSource];
};

const DataSource = (props) => {
  const { dataSourceId } = props;
  const [tableData, setTableData] = useState({
    name: '',
    cols: [],
    rows: [],
    pagination: {},
  });

  const openDataSourceModal = (rowData) => {
    new GeckoModalService().open(() => {
      return (
        <EditDataModal
          dataSourceId={dataSourceId}
          rowData={rowData}
          onSave={onEdit}
        />
      );
    });
  };

  const onEdit = () => {
    refreshDataSource();
  };

  const deleteRow = (row) => {
    const rowId = row.id;
    return GeckoRequest.delete(`/datasource/${dataSourceId}/${rowId}`, row)
      .then(() => {
        refreshDataSource();
      })
      .catch(renderError);
  };

  const ConfirmDeleteModal = (row) => {
    const confirmDeleteBtns = [
      {
        name: 'Cancel',
        handler: () => new GeckoModalService().close(),
      },
      {
        name: 'Remove Row',
        handler: () => {
          new GeckoModalService().close();
          deleteRow(row);
        },
        type: GECKO_CONSTS.BTN_TYPE_DANGER,
      },
    ];

    new GeckoModalService().open(() => {
      return (
        <GeckoModal
          name={'Remove Row from Data Source'}
          btns={confirmDeleteBtns}
        >
          Are you sure you want to remove this row from the Data source?
        </GeckoModal>
      );
    });
  };
  const errorHandler = () => {
    const toast = new GeckoToastService();
    toast.error('Failed to get data source details.');
  };
  const [dataSource, isLoading, refreshDataSource] = useDataSource(
    dataSourceId,
    errorHandler
  );
  const params = useSelector((state) => state.location.params);

  useEffect(() => {
    const { data, name } = dataSource || '';

    const dataSourceRows = [];
    const cols = [];
    let headerCols = [];

    if (data) {
      const rows = Object.keys(data);
      rows.forEach((row) => {
        cols.push(...Object.keys(data[row]));
        dataSourceRows.push({ id: row, ...data[row] });
      });

      const formattedCols = new Set(cols);

      headerCols = [...formattedCols].map((col) => {
        // Table data takes up 90% total to
        // allow for dropdown button and id on other 10%
        const width = 90 / [...formattedCols].length;

        return {
          key: col,
          name: col,
          width: `${width}%`,
        };
      });

      headerCols.unshift({
        key: 'id',
        name: 'id',
        width: `5%`,
      });

      headerCols.push({
        type: GECKO_CONSTS.TABLE_CELL_TYPE_DROPDOWN,
        items: [
          {
            name: 'Edit',
            handler: (row) => openDataSourceModal(row),
          },
          {
            name: 'Remove',
            handler: (row) => ConfirmDeleteModal(row),
          },
        ],
        width: '5%',
      });

      const [formattedRows, pagination] = GeckoPagination.paginate({
        page: params.page,
        order_by: 'name',
      })(dataSourceRows);

      return setTableData({
        name: `${name} Data`,
        cols: headerCols,
        rows: formattedRows,
        pagination: pagination,
      });
    }
    return setTableData({
      name: `${name} Data`,
      cols: [],
      rows: [],
      pagination: {},
    });
  }, [dataSource, params]);

  let attachmentFile = null;

  const processFile = (rawFile) => {
    return GeckoParser.readCsv(rawFile, true)
      .then((rawData) => {
        const columns = rawData.meta.fields;

        const mailMergeTagRegex = new RegExp('^[a-zA-Z0-9_]*$');
        const invalidColumn = columns.find(
          (name) => !mailMergeTagRegex.test(name)
        );

        if (invalidColumn) {
          throw new Error(`Invalid column name: ${invalidColumn}`);
        }

        const { data } = rawData;

        return GeckoRequest.post(`/datasource/${dataSourceId}`, {
          data: { ...data },
        }).then(() => {
          new GeckoToastService().success('Successfully imported data!');
          refreshDataSource();
        });
      })
      .catch((err) => {
        new GeckoToastService().error(err?.message || err);
      });
  };

  const handleSelectedFile = (event) => {
    const files = event.target.files;
    if (!files.length) {
      return console.error('No files selected');
    }

    const oneMegaByteInBytes = 1000000;

    if (files[0].size >= oneMegaByteInBytes) {
      new GeckoToastService().error('File is too large, must be below 1MB');
      return;
    }
    processFile(files[0]);
  };

  const headerBtns = [
    {
      preset: GECKO_CONSTS.BTN_PRESET_IMPORT,
      handler: () => attachmentFile.click(),
    },
  ];

  return (
    <div className="content-margin">
      <GeckoCardTable
        {...tableData}
        headerBtns={tableData.rows.length ? [] : headerBtns}
        rowHandler={openDataSourceModal}
        alert="You don't have any data at the moment."
        newLinkHandler={() => attachmentFile.click()}
      />
      <input
        ref={(ref) => (attachmentFile = ref)}
        onChange={handleSelectedFile}
        style={{ display: 'none' }}
        type="file"
        accept=".csv"
      />
      <Loader isLoading={isLoading} />
    </div>
  );
};

export default DataSource;
